// GLobals
import { memo, useCallback, useMemo, useState } from 'react';
import { Mentions } from 'antd';
import { MentionsOptionProps } from 'antd/es/mentions';

// Components
import { CloseIcon } from '@/icons';
import { Input } from '@/ui';
import { StyledTag, StyledList } from './InputStyles';

type TTagInputProps = {
  id?: string;
  value?: string[];
  type?: string;
  mentionConfig?: {
    prefix?: string;
    options?: MentionsOptionProps[];
    rows?: number;
  };
  placeholder?: string;
  onChange?: (value: string[]) => void;
};

const TagInput = ({ onChange, value = [], type, mentionConfig, placeholder }: TTagInputProps) => {
  const [inputValue, setInputValue] = useState<string>('');

  const handleRemove = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const removed = event.currentTarget.getAttribute('data-value');
      event.stopPropagation();
      event.preventDefault();
      const filtered = value.filter((item) => item !== removed);
      onChange?.(filtered);
    },
    [onChange, value],
  );

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (inputValue) {
        e.preventDefault();
        e.stopPropagation();
        const result = Array.from(new Set([...value, inputValue]));
        onChange?.(result);
        setInputValue('');
      }
    },
    [inputValue, onChange, value],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const list = useMemo(
    () =>
      value?.map((item) => (
        <StyledTag key={item}>
          {item}
          <button type="button" data-value={item} onClick={handleRemove}>
            <CloseIcon />
          </button>
        </StyledTag>
      )),
    [handleRemove, value],
  );

  return (
    <div>
      {mentionConfig ? (
        <Mentions
          options={mentionConfig.options}
          prefix={mentionConfig.prefix}
          rows={mentionConfig.rows}
          onPressEnter={handleKeyDown}
          value={inputValue}
          onChange={setInputValue}
          placeholder={placeholder}
        />
      ) : (
        <Input
          placeholder={placeholder}
          type={type}
          onPressEnter={handleKeyDown}
          value={inputValue}
          onChange={handleChange}
        />
      )}
      <StyledList>{list}</StyledList>
    </div>
  );
};

export default memo(TagInput);
